import React from 'react';
import { ChatEventType, useMessage, useVendor, Vendor } from '@outdoorly/react-chat-sdk';
import { Fragment, useMemo } from 'react';
import type { Message } from '@outdoorly/react-chat-sdk';
import { ChatMessage } from '../Message/ChatMessage';
import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import { EmojiAndLinkifiedText } from '../Message/EmojiAndLinkifiedText';
import { SkeletonChatImage } from '../SkeletonImage';
import { MessageAttributes } from '.';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import dayjs from 'dayjs';
import { MessageEvent, useEvents } from '../Message/MessageEvent';
import { MotionBox } from '../Motion';

export const MessageGroups = ({ messages }: { messages: Message[] }) => {
  const { vendor } = useVendor();

  let groupedMessage: Array<Message> = [];
  const events = useEvents({ labelSubstitutions: {} });
  const filteredMessages = messages?.filter((message) => {
    const messageAttributes = message.attributes as MessageAttributes;
    if (messageAttributes?.type === 'Message' || Object.keys(events[messageAttributes.type] ?? {}).length) {
      return true;
    } else {
      return false;
    }
  });
  return (
    <Fragment>
      {filteredMessages?.map((message, index) => {
        const attributes = message.attributes as MessageAttributes;
        const nextMessageAttributes = filteredMessages[index + 1]?.attributes as MessageAttributes;
        const prevMessage = filteredMessages[index + 1];
        const currentUserId = message.author;
        const nextUserId = filteredMessages[index + 1]?.author;
        const isLastMessage = index === filteredMessages.length - 1;
        if (prevMessage?.author === message.author && attributes.type === 'Message' && !isLastMessage) {
          groupedMessage.push(message);
        } else {
          groupedMessage.push(message);
          const messageGroup = [...groupedMessage];
          groupedMessage = [];

          return (
            <MessageGroupContainer
              key={message.sid}
              messageGroup={messageGroup}
              vendor={vendor}
              isLastGroup={isLastMessage}
              nextGroupIsSameAuthor={currentUserId === nextUserId}
              eventIsPiiCollectionFlow={
                attributes.type === ChatEventType.NoExpertsAvailable &&
                nextMessageAttributes?.type === ChatEventType.PiiRequest
              }
            />
          );
        }
      })}
    </Fragment>
  );
};

export const MessageGroupContainer = ({
  messageGroup,
  vendor,
  isLastGroup,
  nextGroupIsSameAuthor,
  eventIsPiiCollectionFlow
}: {
  messageGroup: Message[];
  vendor?: Vendor;
  isLastGroup: boolean;
  nextGroupIsSameAuthor: boolean;
  eventIsPiiCollectionFlow: boolean;
}) => {
  const firstMessage = messageGroup[0];
  const lastMessage = messageGroup.at(-1)!;
  const attributes = firstMessage.attributes as MessageAttributes;
  const isExpert = firstMessage.author !== 'system' && !!attributes.expert?.expertProfile;

  const isEventOnly = attributes.type !== ChatEventType.Message && firstMessage === lastMessage;
  const isLastMessageAnEvent = (lastMessage.attributes as MessageAttributes).type !== ChatEventType.Message;

  const avatarSource = attributes.expert?.expertProfile?.avatar ?? vendor?.logo;
  const avatarUrl = avatarSource
    ? tryToBuildImgixUrl(avatarSource, {
        w: 50,
        h: 50,
        auto: 'format,compress',
        crop: 'faces',
        fit: 'crop'
      })
    : undefined;

  return (
    <Box key={firstMessage.sid}>
      {/* {lastReadIndex !== -1 && horizonAmount && showHorizonIndex === message.index ? (
  <Horizon horizonRef={horizonRef} amount={horizonAmount} />
) : null}  */}

      {!isEventOnly && (
        <Flex pos="relative" w="100%" key={firstMessage.sid}>
          <MotionBox
            pos="absolute"
            initial={{ bottom: '0px', opacity: 0 }}
            animate={{ bottom: '20px', opacity: 1 }}
            key="avatar"
          >
            {isExpert && (
              <Avatar
                src={avatarUrl}
                w="24px"
                h="24px"
                ignoreFallback
                role="presentation"
                aria-hidden={true}
              />
            )}
          </MotionBox>
          <Flex w="100%" flexDir="column" key="messageGroup">
            {(isLastMessageAnEvent ? messageGroup.slice(0, -1) : messageGroup).map((m, index, array) => (
              <MessageContainer
                key={m.sid}
                message={m}
                isLastMessageInGroup={index === array.length - 1}
                isLastMessage={isLastGroup && index === array.length - 1}
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isLastMessageAnEvent && (
        <MessageEventContainer
          message={lastMessage}
          isLastMessage={isLastGroup}
          isLastMessageInGroup={!nextGroupIsSameAuthor}
          isPiiCollectionFlow={eventIsPiiCollectionFlow}
        />
      )}
    </Box>
  );
};

export const MessageContainer = ({
  message,
  isLastMessage,
  isLastMessageInGroup
}: {
  message: Message;
  isLastMessageInGroup: boolean;
  isLastMessage: boolean;
}) => {
  const attributes = message.attributes as MessageAttributes;
  const isExpertOrSystem = message?.author === 'system' || Boolean(attributes.expert);
  const isExpert = message.author !== 'system' && !!attributes.expert?.expertProfile;

  const body = useMemo(
    () =>
      message?.body ? (
        <Text
          variant="paragraph-medium"
          color={isExpert ? 'scheme.contentPrimary' : 'scheme.contentInversePrimary'}
        >
          <EmojiAndLinkifiedText body={message.body} id={message?.sid ?? ''} />
        </Text>
      ) : message?.attachedMedia && message?.attachedMedia.length > 0 ? (
        <SkeletonChatImage media={message.attachedMedia[0]} />
      ) : (
        <></>
      ),
    [message]
  );

  return (
    <ChatMessage
      body={body}
      isExpertOrSystem={isExpertOrSystem}
      isLastMessageInGroup={isLastMessageInGroup}
      id={message.sid}
      expertName={attributes.expert?.expertProfile?.publicName}
      dateUpdated={dayjs(message.dateUpdated).toString()}
      isLastMessage={isLastMessage}
    />
  );
};

export const MessageEventContainer = ({
  message,
  isLastMessageInGroup,
  isLastMessage,
  isPiiCollectionFlow
}: {
  message: Message;
  isLastMessageInGroup: boolean;
  isLastMessage: boolean;
  isPiiCollectionFlow: boolean;
}) => {
  const attributes = message.attributes as MessageAttributes;

  const email = useMemo(
    () => ({
      censored: attributes.censored,
      completed: attributes.completed,
      skipped: attributes.skipped
    }),
    [attributes.censored, attributes.completed, attributes.skipped]
  );

  return (
    <MessageEvent
      id={message.sid}
      email={email}
      eventType={attributes.type}
      expert={attributes.expert}
      recommendation={attributes.recommendation}
      isLastMessageInGroup={isLastMessageInGroup}
      isLastMessage={isLastMessage}
      dateUpdated={dayjs(message.dateUpdated).toString()}
      isPiiCollectionFlow={isPiiCollectionFlow}
      npsRequestId={attributes.npsRequestId ?? undefined}
      npsRequest={attributes.npsRequest}
      cancelled={attributes.cancelled}
      idleCloseTime={attributes.idleCloseTime}
    />
  );
};
